import { Checkbox, Spinner } from '@mm/components';
import { PlusCircleIcon, TrashIcon } from '@mm/icons';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import {
  PromoterModel,
  PromoterPublicReply,
  ErrorsList,
} from '@mm/state/model/PromoterModel';
import { colors, MakeMemberOptional } from '@mm/utils';
import React, {
  MutableRefObject,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import classnames from 'classnames';
import styles from './PublicReplies.module.css';
import { InputMaxLength } from '../InputMaxLength/InputMaxLength';
import { useTrackReplyOutsideClick } from './use-track-reply-outside-click';
import { DeleteTwoTone } from '@ant-design/icons';

const MAX_REPLY_LENGTH = 540;

type InputRef = MutableRefObject<
  HTMLInputElement | HTMLTextAreaElement | undefined
>;

type Props = {
  promoter: MakeMemberOptional<PromoterModel, 'id' | 'pageId'>;
  chatbotId: number;
  isInstachamp?: boolean;
  customSendReply?: JSX.Element;
  createMultipleRepliesText?: React.ReactElement;
  addReplyBtnText?: string;
  customReplyText?: string;
  emptyReplyText?: string;
  isAutomationsSlideout?: boolean;
  deleteButtonIconColor?: string;
  onRepliesChange?: (
    replies: PromoterPublicReply[],
    sendReply: boolean,
  ) => void;
  customTextareaComponent?: (
    text: string,
    onUpdate: (text: string) => void,
    onClick: () => void,
    maxLength: number,
    editMode: boolean,
    setEditMode: (edit: boolean) => void,
    inputFieldRef: InputRef,
    ref: MutableRefObject<HTMLDivElement | null>,
    disabled?: boolean,
  ) => ReactElement;
  customCheckSendReplyAction?: () => void;
  belowHeader?: JSX.Element;
  hideAddIcon?: boolean;
  css?: {
    addButtonStyle?: string;
    deleteButtonIconStyle?: string;
  };
};

type PublicReplyProps = {
  reply: PromoterPublicReply;
  index: number;
  onDeleteReply: (index: number) => void;
  onUpdateReply: (newReply: PromoterPublicReply, index: number) => void;
  isInstachamp?: boolean;
  customReplyText?: string;
  emptyReplyText?: string;
  isAutomationsSlideout?: boolean;
  customTextareaComponent?: (
    text: string,
    onUpdate: (text: string) => void,
    onClick: () => void,
    maxLength: number,
    editMode: boolean,
    setEditMode: (edit: boolean) => void,
    inputFieldRef: InputRef,
    ref: MutableRefObject<HTMLDivElement | null>,
    disabled?: boolean,
  ) => ReactElement;
  disabled?: boolean;
  deleteButtonIconColor?: string;
  css?: {
    deleteButtonIconStyle?: string;
  };
};

const PublicReply = (props: PublicReplyProps) => {
  const {
    reply,
    index,
    onUpdateReply,
    onDeleteReply,
    isInstachamp,
    customReplyText,
    emptyReplyText,
    customTextareaComponent,
    isAutomationsSlideout,
    deleteButtonIconColor,
    css,
    disabled,
  } = props;
  const [replyText, setReplyText] = useState(reply.text);
  const replyError = ErrorsList(reply.error);
  const [showError, setShowError] = useState(
    !reply.text.length || reply.error?.length,
  );
  const [editMode, setEditModeDumb] = useState(false);

  const setEditMode = (isEditOn: boolean) => {
    if (disabled && isEditOn) {
      return;
    }
    setEditModeDumb(isEditOn);
  };

  const onChange = (text: string) => {
    if (!text.trim().length) {
      setShowError(true);
    } else {
      setShowError(undefined);
    }
    setReplyText(text);
  };

  const onUpdate = (text: string) => {
    if (text !== reply.text) {
      onUpdateReply({ ...reply, text }, index);
    }
  };

  useEffect(() => {
    if (!editMode) {
      onUpdate(replyText);
    }
  }, [editMode]);

  const inputFieldRef = useRef() as InputRef;

  const onQuestionInputClick = () => {
    setTimeout(() => inputFieldRef?.current?.focus(), 200);
    setEditMode(true);
  };

  const { replyInputHotSpotRef } = useTrackReplyOutsideClick({
    editMode,
    setEditMode,
  });

  const deleteBtn = (
    <div
      className={classnames(styles.public_reply_delete, {
        [styles.public_replies_delete_automations_slideout]:
          isAutomationsSlideout,
      })}
      onClick={() => onDeleteReply(index)}
    >
      {isAutomationsSlideout ? (
        <DeleteTwoTone color={deleteButtonIconColor} />
      ) : (
        <TrashIcon
          className={classnames(
            styles.public_reply_trash_icon,
            css?.deleteButtonIconStyle,
          )}
          color={deleteButtonIconColor}
        />
      )}
    </div>
  );

  return (
    <div
      className={classnames(styles.public_reply_container, {
        [styles.public_reply_container_error]: showError,
        [styles.public_reply_container_automations_slideout]:
          isAutomationsSlideout,
      })}
    >
      {!isAutomationsSlideout && (
        <div className={styles.public_reply_header}>
          <div className={styles.public_reply_title}>
            {customReplyText || 'Reply'} {index + 1}
          </div>
          {!isInstachamp && deleteBtn}
        </div>
      )}
      <div
        className={classnames(styles.public_reply_body, {
          [styles.public_replies_body_automations_slideout]:
            isAutomationsSlideout,
        })}
      >
        <div
          className={classnames(styles.public_reply_textarea_wrapper, {
            [styles.public_replies_textarea_wrapper_automations_slideout]:
              isAutomationsSlideout,
          })}
        >
          {customTextareaComponent ? (
            customTextareaComponent(
              reply.text,
              onChange,
              onQuestionInputClick,
              MAX_REPLY_LENGTH,
              editMode,
              setEditMode,
              inputFieldRef,
              replyInputHotSpotRef,
              disabled,
            )
          ) : (
            <InputMaxLength max={MAX_REPLY_LENGTH} value={replyText}>
              <textarea
                className={styles.public_reply_textarea}
                onChange={e => onChange(e.target.value)}
                value={replyText}
                onBlur={() => onUpdate(replyText)}
                maxLength={MAX_REPLY_LENGTH}
                disabled={disabled}
              />
            </InputMaxLength>
          )}

          {showError && isInstachamp && (
            <div
              className={classnames(styles.public_reply_error, {
                [styles.public_replies_reply_error_wrapper_automations_slideout]:
                  isAutomationsSlideout,
              })}
            >
              {emptyReplyText || 'Reply cannot be empty'}
            </div>
          )}
        </div>
        {(isInstachamp || isAutomationsSlideout) && deleteBtn}
      </div>
      {showError && !isInstachamp && (
        <div
          className={classnames(styles.public_reply_error, {
            [styles.public_replies_reply_error_wrapper_automations_slideout]:
              isAutomationsSlideout,
          })}
        >
          {emptyReplyText || replyError || 'Reply cannot be empty'}
        </div>
      )}
    </div>
  );
};

export const PublicReplies = (props: Props) => {
  const {
    promoter,
    isInstachamp,
    customSendReply,
    createMultipleRepliesText,
    addReplyBtnText,
    customReplyText,
    chatbotId,
    onRepliesChange,
    emptyReplyText,
    customTextareaComponent,
    customCheckSendReplyAction,
    belowHeader,
    hideAddIcon,
    isAutomationsSlideout = false,
    css,
    deleteButtonIconColor,
  } = props;
  const promoterCRUDActions = usePromoterCRUDActions();
  const [isLoading, setIsLoading] = useState(false);
  const [sendPublicReply, setSendPublicReply] = useState(
    !!promoter?.sendPublicReply,
  );

  useEffect(() => {
    if (sendPublicReply !== !!promoter?.sendPublicReply) {
      setSendPublicReply(!!promoter?.sendPublicReply);
    }
  }, [!!promoter?.sendPublicReply]);

  const [publicReplies, setPublicReplies] = useState<PromoterPublicReply[]>([
    { text: '' },
  ]);

  useEffect(() => {
    if (promoter?.publicReplies?.length) {
      setPublicReplies(promoter.publicReplies);
    } else {
      setPublicReplies([{ text: '' }]);
    }
  }, []);

  const updatePromoter = async (
    replies: PromoterPublicReply[],
    sendReply: boolean,
  ) => {
    setIsLoading(true);
    onRepliesChange?.(replies, sendReply);
    setPublicReplies(replies);
    if (promoter.id) {
      await promoterCRUDActions.update(
        chatbotId,
        {
          ...promoter,
          sendPublicReply: sendReply,
          publicReplies: replies,
        },
        false,
      );
    }
    setIsLoading(false);
  };

  const toggleSendPublicReply = () => {
    if (customCheckSendReplyAction) {
      customCheckSendReplyAction();
    } else {
      updatePromoter(
        publicReplies.length ? publicReplies : [{ text: '' }],
        !sendPublicReply,
      );
    }
    setSendPublicReply(!sendPublicReply);
  };

  const onAddReply = () => {
    setPublicReplies([...publicReplies, { text: '' }]);
  };

  const onUpdateReply = (newReply: PromoterPublicReply, index: number) => {
    const newReplies = [...publicReplies];
    newReplies.splice(index, 1, newReply);
    setPublicReplies(newReplies);
    updatePromoter(newReplies, sendPublicReply);
  };

  const onDeleteReply = (index: number) => {
    const newReplies = [...publicReplies];
    newReplies.splice(index, 1);
    setPublicReplies(newReplies);
    updatePromoter(newReplies, newReplies.length ? sendPublicReply : false);
  };

  return (
    <div
      className={classnames(styles.public_replies_container, {
        [styles.public_replies_container_instachamp]: isInstachamp,
        [styles.public_replies_container_automations_slideout]:
          isAutomationsSlideout,
      })}
    >
      <Checkbox
        isChecked={sendPublicReply}
        className={styles.public_reply_checkbox}
        label={customSendReply || 'Send Public Reply'}
        onToggle={toggleSendPublicReply}
      />
      {belowHeader}
      {sendPublicReply && (
        <>
          {createMultipleRepliesText || (
            <p className={styles.public_replies_description}>
              {'Create multiple replies. We’ll send them at random'}
            </p>
          )}
          {publicReplies.map((reply, index) => {
            return (
              <PublicReply
                disabled={!sendPublicReply}
                key={`${reply.text}_${index}`}
                isInstachamp={isInstachamp}
                reply={reply}
                index={index}
                onDeleteReply={onDeleteReply}
                onUpdateReply={onUpdateReply}
                emptyReplyText={emptyReplyText}
                customReplyText={customReplyText}
                customTextareaComponent={customTextareaComponent}
                isAutomationsSlideout={isAutomationsSlideout}
                deleteButtonIconColor={deleteButtonIconColor}
                css={css}
              />
            );
          })}
          <div
            onClick={onAddReply}
            className={classnames(
              styles.add_public_reply_btn,
              {
                [styles.add_public_reply_btn_disabled]: isLoading,
              },
              css?.addButtonStyle,
            )}
          >
            {isLoading ? (
              <Spinner
                show={isLoading}
                color="#A8A8A8"
                width={16}
                size={16}
                position="relative"
                backgroundColor="transparent"
              />
            ) : hideAddIcon ? null : (
              <PlusCircleIcon
                color={isInstachamp ? colors.instachampPurple : colors.blue}
              />
            )}
            {addReplyBtnText || 'Add new Reply'}
          </div>
        </>
      )}
    </div>
  );
};
